import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchFoodSourceDiscovery } from "../../../../api/foodApi";
import { getFoodDiscoveryOutcome } from "../../../../constants/foodDiscoveryOutcomeLabels";
import SourceHealthBadge from "../SourceHealthBadge";
import AdminTableContainer from "../layout/AdminTableContainer";
import FoodSourceDiscoveryReviewPanel from "./FoodSourceDiscoveryReviewPanel";
import FoodRetailSourceSelectionPanel from "./FoodRetailSourceSelectionPanel";

function formatBool(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "—";
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function discoveryOutcomeLabel(source) {
  return source.discoveryOutcome?.label || getFoodDiscoveryOutcome(source).label;
}

function discoveryOutcomeClass(source) {
  const tone = source.discoveryOutcome?.tone || getFoodDiscoveryOutcome(source).tone;
  return `food-discovery-admin__outcome food-discovery-admin__outcome--${tone}`;
}

const FoodSourceDiscoveryPage = ({ onClose }) => {
  const [sources, setSources] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const loadSources = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchFoodSourceDiscovery();
      setSources(data.sources || []);
    } catch (error) {
      setSources([]);
      setErrorMessage(error.message || "Unable to load food source discovery records.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  const selectedSource = useMemo(
    () => sources.find((source) => source.sourceKey === selectedKey) || null,
    [sources, selectedKey]
  );

  const araOutcome = useMemo(() => {
    const ara = sources.find((source) => source.sourceKey === "ARA_COLOMBIA");
    return ara ? getFoodDiscoveryOutcome(ara) : null;
  }, [sources]);

  const handleSaved = (updated) => {
    setSources((current) =>
      current.map((source) =>
        source.sourceKey === updated.sourceKey ? { ...source, ...updated } : source
      )
    );
  };

  return (
    <div className="admin-dashboard admin-food-discovery-dash admin-food-discovery-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Food Supermarket Discovery</h1>
          <p className="admin-dashboard__subtitle">
            Manual review of public price availability — no scraping or ingestion in this
            milestone
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadSources}
            disabled={loading}
          >
            Refresh
          </button>
          {onClose ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
              Back
            </button>
          ) : null}
        </div>
      </header>

      {errorMessage ? <p className="muted">{errorMessage}</p> : null}
      {loading ? <p className="muted">Loading supermarket discovery records…</p> : null}

      {araOutcome ? (
        <div className={`food-discovery-admin__banner ${discoveryOutcomeClass({ discoveryOutcome: araOutcome })}`}>
          <strong>ARA Colombia review:</strong> {araOutcome.label}
          <span className="muted"> — Recommendation: {araOutcome.recommendation}</span>
        </div>
      ) : null}

      <FoodRetailSourceSelectionPanel />

      <div className="food-discovery-admin__layout">
        <AdminTableContainer>
          <table className="admin-data-health-table food-discovery-admin__table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Discovery</th>
                <th>Review outcome</th>
                <th>Public price</th>
                <th>Location</th>
                <th>Login</th>
                <th>App only</th>
                <th>Bot/CAPTCHA</th>
                <th>Access</th>
                <th>Terms</th>
                <th>Risk</th>
                <th>Pri</th>
                <th>Last discovered</th>
                <th>Health</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sources.map((source) => (
                <tr
                  key={source.sourceKey}
                  className={
                    selectedKey === source.sourceKey ? "food-discovery-admin__row--active" : ""
                  }
                >
                  <td>
                    <strong>{source.sourceName}</strong>
                    <span className="admin-data-health-code">{source.sourceKey}</span>
                  </td>
                  <td>{source.discoveryStatus}</td>
                  <td>
                    <span className={discoveryOutcomeClass(source)}>
                      {discoveryOutcomeLabel(source)}
                    </span>
                  </td>
                  <td>{formatBool(source.publicPriceAvailable)}</td>
                  <td>{formatBool(source.locationRequired)}</td>
                  <td>{formatBool(source.loginRequired)}</td>
                  <td>{formatBool(source.appOnly)}</td>
                  <td>{formatBool(source.captchaOrBotProtectionDetected)}</td>
                  <td>{source.recommendedAccessMethod}</td>
                  <td>{source.termsReviewStatus}</td>
                  <td>{source.ingestionRiskLevel}</td>
                  <td>{source.ingestionPriority ?? "—"}</td>
                  <td>{formatDate(source.lastDiscoveredAt)}</td>
                  <td>
                    <SourceHealthBadge status={String(source.healthStatus || "").toLowerCase()} />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="admin-dashboard__action-btn"
                      onClick={() => setSelectedKey(source.sourceKey)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>

        <FoodSourceDiscoveryReviewPanel
          source={selectedSource}
          onSaved={handleSaved}
          onClose={() => setSelectedKey(null)}
        />
      </div>
    </div>
  );
};

export default FoodSourceDiscoveryPage;
